import React from 'react';
import { GraduationCap, Award, Globe, Music, Camera } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { portfolioData } from '../mock';

const About = () => {
  const { personal, education } = portfolioData;

  return (
    <section id="about" className="py-16 sm:py-24 bg-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              About Me
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">Get to know me better</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Personal Info Card */}
            <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Professional Summary</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {personal.summary}
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                      <Globe size={18} className="text-cyan-400" />
                      Languages
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {personal.languages.map((lang, idx) => (
                        <span
                          key={idx}
                          className="text-sm px-3 py-1 bg-cyan-400/10 text-cyan-400 rounded-full border border-cyan-400/30"
                        >
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                      <Music size={18} className="text-cyan-400" />
                      Interests
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {personal.hobbies.map((hobby, idx) => (
                        <span
                          key={idx}
                          className="text-sm px-3 py-1 bg-slate-700 text-gray-300 rounded-full"
                        >
                          {hobby}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Card */}
            <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Quick Stats</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Experience</p>
                      <p className="text-2xl font-bold text-cyan-400">4+ Years</p>
                    </div>
                    <div className="w-16 h-16 bg-cyan-400/10 rounded-full flex items-center justify-center">
                      <Award className="text-cyan-400" size={32} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Companies Worked</p>
                      <p className="text-2xl font-bold text-cyan-400">4+</p>
                    </div>
                    <div className="w-16 h-16 bg-cyan-400/10 rounded-full flex items-center justify-center">
                      <GraduationCap className="text-cyan-400" size={32} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Projects</p>
                      <p className="text-2xl font-bold text-cyan-400">50+</p>
                    </div>
                    <div className="w-16 h-16 bg-cyan-400/10 rounded-full flex items-center justify-center">
                      <Camera className="text-cyan-400" size={32} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Education Timeline */}
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
              Education
            </h3>
            <div className="space-y-6">
              {education.map((edu) => (
                <Card
                  key={edu.id}
                  className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300"
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="bg-cyan-400/10 p-2 rounded-lg mt-1">
                            <GraduationCap className="text-cyan-400" size={24} />
                          </div>
                          <div>
                            <h4 className="text-xl font-bold text-white mb-1">{edu.degree}</h4>
                            {edu.field && (
                              <p className="text-cyan-400 font-medium mb-1">{edu.field}</p>
                            )}
                            <p className="text-gray-400">{edu.institution}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-start sm:items-end gap-2">
                        <span className="text-gray-400 text-sm bg-slate-700/50 px-3 py-1 rounded-full">
                          {edu.duration}
                        </span>
                        <span className="text-cyan-400 font-semibold">
                          {edu.percentage}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
